import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { aiApi } from '../../../api/ai.js';
import { ApiError } from '../../../api/client.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { Card } from '../../../components/ui/Card.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Switch } from '../../../components/ui/Switch.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Textarea } from '../../../components/ui/Textarea.jsx';
import { Skeleton } from '../../../components/ui/Skeleton.jsx';
import { t } from '../../../lib/i18n.js';

const TOOL_LABELS = {
  searchProducts: 'Ricerca prodotti',
  getInventory: 'Disponibilità e scorte',
  searchKnowledge: 'Knowledge base (RAG)',
  getStoreInfo: 'Informazioni negozio',
  compareProducts: 'Confronto prodotti'
};

function AgentCard({ agent, toolCatalog, chatModels, defaultModelId, canManage, onSaved }) {
  const customPrompt = agent.systemPrompt && agent.systemPrompt !== agent.defaultSystemPrompt;

  const [enabled, setEnabled] = useState(agent.enabled);
  const [tools, setTools] = useState(agent.tools || []);
  const [model, setModel] = useState(agent.model || '');
  const [temperature, setTemperature] = useState(agent.temperature == null ? '' : String(agent.temperature));
  const [systemPrompt, setSystemPrompt] = useState(customPrompt ? agent.systemPrompt : '');
  const [showDefault, setShowDefault] = useState(false);

  useEffect(() => {
    const custom = agent.systemPrompt && agent.systemPrompt !== agent.defaultSystemPrompt;
    setEnabled(agent.enabled);
    setTools(agent.tools || []);
    setModel(agent.model || '');
    setTemperature(agent.temperature == null ? '' : String(agent.temperature));
    setSystemPrompt(custom ? agent.systemPrompt : '');
  }, [agent]);

  const mutation = useMutation({
    mutationFn: () => aiApi.setAgent(agent.id, {
      enabled,
      tools,
      model: model || '',
      temperature: temperature === '' ? null : Number(temperature),
      systemPrompt: systemPrompt || ''
    }),
    onSuccess: () => { toast.success(`"${agent.label}" salvato.`); onSaved(); },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const toggleTool = (name) => setTools((cur) => (cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name]));

  const initial = {
    enabled: agent.enabled,
    tools: (agent.tools || []).slice().sort().join(','),
    model: agent.model || '',
    temperature: agent.temperature == null ? '' : String(agent.temperature),
    systemPrompt: customPrompt ? agent.systemPrompt : ''
  };
  const dirty = enabled !== initial.enabled
    || tools.slice().sort().join(',') !== initial.tools
    || model !== initial.model
    || temperature !== initial.temperature
    || systemPrompt !== initial.systemPrompt;

  return (
    <Card>
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-ink">{agent.label}</h2>
            {!enabled && <Badge tone="neutral">disattivato</Badge>}
          </div>
          <p className="text-xs text-ink-muted">{agent.description}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xs text-ink-muted">Attivo</span>
          <Switch checked={enabled} disabled={!canManage} onChange={setEnabled} />
        </div>
      </header>

      <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Select label="Modello" value={model} disabled={!canManage} onChange={(e) => setModel(e.target.value)} hint="Modello usato da questo agente.">
          <option value="">Predefinito{defaultModelId ? ` (${defaultModelId})` : ''}</option>
          {chatModels.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </Select>
        <Input label="Temperatura" type="number" min="0" max="2" step="0.1" placeholder="Predefinita" value={temperature} disabled={!canManage} onChange={(e) => setTemperature(e.target.value)} hint="0 = preciso, 2 = creativo. Vuoto = predefinita." />
      </div>

      <div className="mb-5">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-sm font-medium text-ink">Istruzioni dell'agente (system prompt)</span>
          <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => setShowDefault((v) => !v)}>
            {showDefault ? 'Nascondi predefinito' : 'Mostra predefinito'}
          </button>
        </div>
        <Textarea rows={5} placeholder="Lascia vuoto per usare le istruzioni predefinite di questo agente." value={systemPrompt} disabled={!canManage} onChange={(e) => setSystemPrompt(e.target.value)} />
        {showDefault && (
          <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-surface p-3 text-xs text-ink-muted">{agent.defaultSystemPrompt || '-'}</pre>
        )}
      </div>

      <p className="mb-2 text-sm font-medium text-ink">Abilità (strumenti)</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {toolCatalog.map((tool) => {
          const on = tools.includes(tool.name);
          return (
            <label key={tool.name} className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg border px-3 py-2 transition-colors ${on ? 'border-ink/30 bg-surface-subtle' : 'border-border bg-surface'} ${!canManage ? 'cursor-default opacity-80' : ''}`}>
              <span className="min-w-0">
                <span className="block text-sm text-ink">{TOOL_LABELS[tool.name] || tool.name}</span>
                <span className="block truncate text-xs text-ink-faint" title={tool.description}>{tool.name}</span>
              </span>
              <Switch checked={on} disabled={!canManage} onChange={() => toggleTool(tool.name)} />
            </label>
          );
        })}
      </div>

      {canManage && (
        <div className="mt-4 flex justify-end">
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={!dirty}>Salva</Button>
        </div>
      )}
    </Card>
  );
}

export function AgentsPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const canManage = hasPermission('ai:configure');

  const query = useQuery({ queryKey: ['ai', 'agents'], queryFn: aiApi.getAgents, enabled: hasPermission('ai:configure') });
  const agents = query.data?.agents || [];
  const toolCatalog = query.data?.toolCatalog || [];
  const chatModels = query.data?.chatModels || [];
  const defaultModelId = query.data?.defaultModelId || null;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['ai', 'agents'] });

  if (!hasPermission('ai:configure')) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Agenti" />
        <Card><p className="text-sm text-ink-muted">Permesso <code>ai:configure</code> richiesto.</p></Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Agenti" description="Per ogni agente dell'assistente: attivazione, modello, temperatura, istruzioni e abilità (inclusa la knowledge base / RAG)." />

      {query.isLoading ? (
        <div className="flex flex-col gap-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-72 w-full" />)}</div>
      ) : (
        <div className="flex flex-col gap-4">
          {agents.map((agent) => (
            <AgentCard key={agent.id} agent={agent} toolCatalog={toolCatalog} chatModels={chatModels} defaultModelId={defaultModelId} canManage={canManage} onSaved={invalidate} />
          ))}
        </div>
      )}
    </div>
  );
}
